import { uiText } from '../../i18n/uiText';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Bell,
  Building2,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  RefreshCw,
} from 'lucide-react';
import PageIntro from '../ui/PageIntro';
import StatusBadge from '../ui/StatusBadge';
import Badge from '../ui/Badge';
import { formatDate, formatRelativeDueDate, getFullName } from '../../utils/formatters';
import { safeInternalRoute } from '../../utils/safeNavigation';

function LoadingCards() {
  return (
    <div className="auditee-card-grid" aria-label={uiText("Загрузка")}>
      {[0, 1, 2].map((item) => <div key={item} className="auditee-skeleton-card" />)}
    </div>
  );
}

function EmptyPanel({ title, text, icon: Icon = CheckCircle2 }) {
  return (
    <div className="auditee-empty-panel">
      <span className="auditee-empty-icon"><Icon size={20} /></span>
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}

export default function AuditeeDashboard({ user, data, loading, error, onReload }) {
  const attentionAudits = data?.attention?.audits || [];
  const attentionIssues = data?.attention?.issues || [];
  const activeAudits = data?.active_audits || [];
  const deadlines = data?.deadlines || [];
  const notifications = data?.notifications || [];
  const unreadNotifications = notifications.filter((item) => item.unread);
  const company = data?.my_company || null;

  const selfCheck = (data?.onboarding || []).find((item) => (
    ['self_check', 'self-check'].includes(item.id) || ['self_check', 'self-check'].includes(item.key)
  ));

  const actions = useMemo(() => {
    const items = [];
    if (selfCheck && !selfCheck.done) {
      items.push({
        key: 'self-check',
        icon: ClipboardCheck,
        title: uiText("Заполните анкету организации"),
        text: uiText("Ответьте на вопросы Self-check. Данные сохраняются автоматически и доступны вашей команде."),
        to: '/self-check',
        label: uiText("Продолжить заполнение"),
        tone: 'primary',
      });
    }
    if (attentionIssues.length) {
      const issue = attentionIssues[0];
      const auditId = issue.audit?.id || issue.audit_id;
      items.push({
        key: 'issues',
        icon: AlertCircle,
        title: uiText("Замечания требуют ответа: {v0}", {v0: (attentionIssues.length)}),
        text: uiText("Откройте замечания, добавьте пояснения или подтверждающие материалы."),
        to: auditId ? `/audits/${auditId}?tab=issues` : '/issues',
        label: uiText("Открыть замечания"),
        tone: 'warning',
      });
    } else if (attentionAudits.length) {
      const audit = attentionAudits[0];
      items.push({
        key: 'audit',
        icon: AlertCircle,
        title: uiText("Есть аудит, требующий вашего участия"),
        text: audit.title || uiText("Аудит #{v0}", {v0: (audit.id)}),
        to: `/audits/${audit.id}`,
        label: uiText("Перейти к аудиту"),
        tone: 'warning',
      });
    }
    if (unreadNotifications.length) {
      items.push({
        key: 'notifications',
        icon: Bell,
        title: uiText("Новые уведомления: {v0}", {v0: (unreadNotifications.length)}),
        text: unreadNotifications[0]?.title || uiText("Посмотрите последние изменения по вашим аудитам."),
        to: '/notifications',
        label: uiText("Посмотреть"),
        tone: 'neutral',
      });
    }
    if (!items.length && deadlines.length) {
      const deadline = deadlines[0];
      items.push({
        key: 'deadline',
        icon: CalendarDays,
        title: uiText("Ближайший срок"),
        text: `${deadline.title || uiText("Действие по аудиту")} · ${formatRelativeDueDate(deadline.date)}`,
        to: deadline.to || '/audits',
        label: uiText("Открыть"),
        tone: 'neutral',
      });
    }
    return items.slice(0, 3);
  }, [attentionAudits, attentionIssues, deadlines, selfCheck, unreadNotifications]);

  return (
    <div className="workspace-page auditee-portal-page">
      <PageIntro
        eyebrow={uiText("Личный кабинет")}
        title={uiText("Здравствуйте, {v0}", {v0: (user?.full_name || uiText("пользователь"))})}
        description={uiText("Здесь только то, что относится к вашей организации и требует вашего участия.")}
        aside={(
          <button type="button" className="btn btn-outline" onClick={onReload} aria-label={uiText("Обновить данные")}>
            <RefreshCw size={15} />{uiText(" Обновить")}</button>
        )}
      />

      {error ? <div className="alert alert-error">{error}</div> : null}

      <section className="auditee-section" aria-labelledby="auditee-actions-heading">
        <div className="auditee-section-heading">
          <div>
            <h2 id="auditee-actions-heading">{uiText("Что нужно сделать")}</h2>
            <p>{uiText("Самые важные действия собраны в одном месте.")}</p>
          </div>
          {actions.length ? <Badge variant="warning">{actions.length}</Badge> : null}
        </div>
        {loading ? <LoadingCards /> : actions.length ? (
          <div className="auditee-card-grid">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <Link key={action.key} to={safeInternalRoute(action.to, '/dashboard')} className={`auditee-action-card is-${action.tone}`}>
                  <span className="auditee-action-icon"><Icon size={20} /></span>
                  <div className="auditee-action-copy">
                    <strong>{action.title}</strong>
                    <p>{action.text}</p>
                    <span>{action.label} <ArrowRight size={14} /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <EmptyPanel
            title={uiText("На сегодня обязательных действий нет")}
            text={uiText("Если аудитор назначит задачу, запросит документ или оставит замечание, оно появится здесь.")}
          />
        )}
      </section>

      <div className="auditee-content-grid">
        <section className="auditee-section" aria-labelledby="auditee-audits-heading">
          <div className="auditee-section-heading">
            <div>
              <h2 id="auditee-audits-heading">{uiText("Мои аудиты")}</h2>
              <p>{uiText("Текущие проверки вашей организации.")}</p>
            </div>
            <Link className="auditee-text-link" to="/audits">{uiText("Все аудиты ")}<ArrowRight size={14} /></Link>
          </div>
          {loading ? <LoadingCards /> : activeAudits.length ? (
            <div className="auditee-audit-list">
              {activeAudits.slice(0, 4).map((audit) => (
                <Link key={audit.id} to={`/audits/${audit.id}`} className="auditee-audit-row">
                  <div className="auditee-audit-row-main">
                    <strong>{audit.title || uiText("Аудит #{v0}", {v0: (audit.id)})}</strong>
                    <span>{audit.site_name || getFullName(audit.customer, uiText("Ваша организация"))}</span>
                  </div>
                  <div className="auditee-audit-row-meta">
                    <span><CalendarDays size={14} /> {formatDate(audit.scheduled_date)}</span>
                    <StatusBadge status={audit.status} type="audit" />
                    <ArrowRight size={16} className="auditee-row-arrow" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyPanel
              title={uiText("Активных аудитов пока нет")}
              text={uiText("Запланированная проверка появится здесь после назначения аудитором.")}
              icon={CalendarDays}
            />
          )}
        </section>

        <aside className="auditee-section auditee-company-panel" aria-labelledby="auditee-company-heading">
          <div className="auditee-section-heading">
            <div>
              <h2 id="auditee-company-heading">{uiText("Моя организация")}</h2>
              <p>{uiText("Данные и доступы сотрудников.")}</p>
            </div>
          </div>
          <span className="auditee-company-icon"><Building2 size={22} /></span>
          <strong className="auditee-company-name">{company?.name || company?.legal_name || uiText("Организация")}</strong>
          <p className="auditee-company-meta">
            {[company?.inn ? uiText("ИНН {v0}", {v0: (company.inn)}) : null, company?.email].filter(Boolean).join(' · ') || uiText("Основные данные организации")}
          </p>
          <div className="auditee-company-links">
            <Link className="btn btn-primary" to="/my-organization">{uiText("Сотрудники и данные")}</Link>
            <Link className="btn btn-outline" to="/self-check">{uiText("Анкета Self-check")}</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
